import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo
} from "react";
import { useMediaQuery } from "react-responsive";
import { withRouter, useHistory, useLocation } from "react-router-dom";
import { theme } from "../../globalStyles";
import { setPromptNode } from "../../utils/promptStore";

import QueuePane from "./QueuePane";
import Waveform from "../Waveform";
import Toast from "../Toast/index";
import useModal from "../../hooks/useModal";
import DownloadModal from "../DownloadModal";
import CreateEditPlaylistModal from "../CreateEditPlaylistModal";
import AddToPlaylistModal from "../AddToPlaylistModal";

import { QueueContext } from "../../context/QueueProvider";
import { AuthContext } from "../../context/AuthProvider";
import { CartContext } from "../../context/CartContext/CartProvider";
import { SearchContext } from "../../context";
import { FavoritesContext } from "../../context";

import {
  Container,
  MobileContainer,
  PlaybackControlsContainer,
  QueueButtonWrapper
} from "./styles";

import pause from "../../assets/pause.svg";
import play from "../../assets/play.svg";
import playNext from "../../assets/play_next.svg";
import playPrevious from "../../assets/play_previous.svg";
import IconButtonV2 from "../IconButtonV2";
import ActionIconStrip from "../ActionIconStrip";
import EditorPopoverButton from "../EditorPopoverButton";
import { EditorContext } from "../../editor/context/EditorContext/EditorProvider";
import { MasterVolumeControl } from "../MasterVolumeControl";
import { timeFormatterNum } from "../../utils/timeFormatter";
import { connectAnalyser } from "../Waveform/audioAnalyser";

// !The <audio> tag used to play the app's audio is at the bottom of this component

const Player = props => {
  const [currentTime, setCurrentTime] = useState(timeFormatterNum(0));
  const [playerActionsPosition, setPlayerActionsPosition] = useState(0);
  const [queueToastPosition, setqueueToastPosition] = useState(0);
  const [duration, setDuration] = useState(timeFormatterNum(0));
  const [playlistVisible, setPlaylistVisible] = useState(false);
  const [mobilePlaylistVisible, setMobilePlaylistVisible] = useState(false);
  const [lastPlayedTrack, setLastPlayedTrack] = useState();
  const [tracksToSave, setTracksToSave] = useState();

  const { isFoundInCart, addCartItem, removeCartItem } = useContext(
    CartContext
  );
  const search = useContext(SearchContext);
  const fav = useContext(FavoritesContext);
  const playlistEl = useRef();
  const audioPlayerEl = useRef(null);
  const isInitialMount = useRef(true);
  const addtoQueuePosition = useRef();
  const playerActionsRef = useRef();
  const queue = useContext(QueueContext);
  const auth = useContext(AuthContext);
  const editor = useContext(EditorContext);
  const isGuest = auth.user.role === "guest";
  const history = useHistory();
  const location = useLocation();
  const promptSignup = useCallback((node) => {
    setPromptNode(node);
    history.push('/signup', { background: location });
  }, [history, location]);

  const [currentTrack, setCurrentTrack] = useState(
    queue.currentTrack && queue.currentTrack.track
      ? queue.currentTrack.track
      : null
  );

  const [queuePulseActive, setQueuePulseActive] = useState(false);
  const queuePulseTimer = useRef(null);
  useEffect(() => {
    if (!queue.addToQueueMessage) return;
    setQueuePulseActive(true);
    clearTimeout(queuePulseTimer.current);
    queuePulseTimer.current = setTimeout(() => setQueuePulseActive(false), 2000);
  }, [queue.addToQueueMessage]);

  ///// MODALS
  const downloadModal = useModal();
  const createEditPlaylistModal = useModal();
  const addToPlaylistModal = useModal();

  // Media Query for Mobile version
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 800 });

  const togglePlay = useCallback(() => {
    // qeusetPlaying(!playing);
    queue.setPlaying(!queue.playing);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.playing]);

  const handlePressSpace = useCallback(
    event => {
      event.stopPropagation();

      if (
        event.keyCode === 32 &&
        event.currentTarget.activeElement.tagName !== "INPUT" &&
        event.currentTarget.activeElement.tagName !== "TEXTAREA"
      ) {
        event.preventDefault();
        togglePlay();
      }
    },
    [togglePlay]
  );

  // finding the More Actions (or the ...) icon's position
  useEffect(() => {
    const playerActions = playerActionsRef.current;
    if (playerActions && !playerActionsPosition) {
      setPlayerActionsPosition(playerActions.getBoundingClientRect().right);

      window.addEventListener("resize", () =>
        setPlayerActionsPosition(playerActions.getBoundingClientRect().right)
      );
    }

    return () => {
      window.removeEventListener("resize", () =>
        setPlayerActionsPosition(playerActions.getBoundingClientRect().right)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // finding the Queue pane icon's position
  useEffect(() => {
    const playlistToggle = addtoQueuePosition.current;
    playlistToggle &&
      setqueueToastPosition(playlistToggle.getBoundingClientRect().left);

    if (playlistToggle && !queueToastPosition) {
      window.addEventListener("resize", () =>
        setqueueToastPosition(playlistToggle.getBoundingClientRect().left)
      );
    }

    return () => {
      window.removeEventListener("resize", () =>
        setqueueToastPosition(playlistToggle.getBoundingClientRect().left)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistVisible, queue.addToQueueMessage]);

  // update play/pause based on Queue Context
  useEffect(() => {
    const currentAudioPlayer = audioPlayerEl.current;
    queue.playing
      ? currentAudioPlayer.play().catch(() => {
        queue.setPlaying(false);
      })
      : currentAudioPlayer.pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.playing]);

  // Event listener to close the QueuePane
  // Also scrolls to the queue section in the QueuePane
  useEffect(() => {
    if (playlistVisible) {
      document.addEventListener("mousedown", closePlaylist);

      const queueSection = document.getElementById("queue-section");
      queueSection.scrollIntoView(true);
    } else {
      document.removeEventListener("mousedown", closePlaylist);
    }

    return () => {
      document.removeEventListener("mousedown", closePlaylist);
    };
  }, [playlistVisible]);

  // MobileView - Scroll to the queue section in the QueuePane
  useEffect(() => {
    if (mobilePlaylistVisible) {
      const queueSection = document.getElementById("queue-section");
      queueSection.scrollIntoView(true);
    }
  }, [mobilePlaylistVisible]);

  // Start playing if a track has changed
  useEffect(() => {
    const currentAudioPlayer = audioPlayerEl.current;
    queue.playing &&
      currentAudioPlayer.play().catch(() => {
        queue.setPlaying(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.playing, queue.currentTrack.track]);

  // Play/Pause on pressing Spacebar
  useEffect(() => {
    if (!editor.drawerState.visible) {
      document.addEventListener("keydown", handlePressSpace);
    } else {
      document.removeEventListener("keydown", handlePressSpace);
    }

    return () => {
      document.removeEventListener("keydown", handlePressSpace);
    };
  }, [handlePressSpace, queue.playing, editor.drawerState.visible]);

  // Storing the last played track for use incase of any encountered failure.
  useEffect(() => {
    if (queue.currentTrack.track) {
      let newCurrentTrack = { ...queue.currentTrack.track };
      setLastPlayedTrack(newCurrentTrack);
    }

    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (!editor.drawerState.visible) {
      queue.setPlaying(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.currentTrack.track]);

  useEffect(() => {
    setCurrentTrack(queue.currentTrack.track);
  }, [queue.currentTrack.track]);

  const closePlaylist = e => {
    if (playlistEl.current && playlistEl.current.contains(e.target)) {
      // inside click
      return;
    }
    // outside click
    setTimeout(() => setPlaylistVisible(false), 100);
  };

  const setCurrentTimeAndDuration = () => {
    const currentAudioPlayer = audioPlayerEl.current;

    const formatedTime = timeFormatterNum(currentAudioPlayer.currentTime);
    const formatedDuration = timeFormatterNum(currentAudioPlayer.duration);
    if (currentTime !== formatedTime)
      setCurrentTime(formatedTime);
    if (duration !== formatedDuration)
      setDuration(formatedDuration);
  };

  const showTime = () => {
    const currentAudioPlayer = audioPlayerEl.current;

    // Not showing track duration if audio metadata is not retrieved
    if (currentAudioPlayer.readyState === 0) {
      /* 
        When the audio file src is changed set the seekbar(percentagePlayed) back to 0.
        audioDOM's readyState is 0 before the audio file is retrieved. Using this as an indication that the track has been changed
      */
      // queue.setPercentagePlayedRef(0);
      return;
    }

    if (queue.seeked) {
      const currentAudioPlayer = audioPlayerEl.current;
      if (currentAudioPlayer.readyState >= 1) {
        currentAudioPlayer.currentTime =
          currentAudioPlayer.duration * (queue.percentagePlayedRef.current / 100);

        queue.setPlaying(true);
        queue.setSeeked(false);
      }
    }
    else {
      let percent =
        (currentAudioPlayer.currentTime / currentAudioPlayer.duration) * 100;
      queue.setPercentagePlayedRef(percent);
    }
    setCurrentTimeAndDuration();
  };

  const playNextAndResetProgress = () => {
    queue.playlist[queue.currentTracker + 1] && queue.playNext();
    setCurrentTime(0);
    queue.playlist[queue.currentTracker + 1] || queue.setPlaying(false);
  };

  const mobileActions = useMemo(() =>
    queue.queueTracker > 0
      ? [{
        type: "queue",
        tooltipText: "Queue",
        label: "Queue",
        selected: mobilePlaylistVisible,
        onClick: () => setMobilePlaylistVisible((v) => !v),
      }]
      : [],
    [queue.queueTracker, mobilePlaylistVisible]
  );
  const desktopActions = useMemo(() => [
    {
      type: "volume",
      render: ({ size, style }) => (
        <MasterVolumeControl
          audioPlayerEl={audioPlayerEl}
          size={size}
          buttonStyle={style}
        />
      ),
    },
    ...(queue.queueTracker > 0
      ? [
        {
          type: "queue",
          tooltipText: "Queue",
          render: ({ size, style }) => (
            <QueueButtonWrapper
              ref={addtoQueuePosition}
              $pulse={queuePulseActive}
            >
              <IconButtonV2
                type="queue"
                size={size}
                style={style}
                selected={playlistVisible}
                onClick={() =>
                  !playlistVisible && setPlaylistVisible(true)
                }
                label="Playlist queue"
                tooltip
                tooltipText="Queue"
              />
            </QueueButtonWrapper>
          ),
        },
      ]
      : []),
    {
      type: "download",
      tooltipText: "Download",
      label: "Download",
      onClick: () => {
        search.setDownloadTrack(queue.currentTrack.track);
        downloadModal.open();
      },
    },
    {
      type: "favorite",
      tooltipText: isGuest || !queue.currentTrack.track.favorite ? "Add to Favorites" : "Remove from Favorites",
      label: "Add to Favorites",
      selected: !!queue.currentTrack.track.favorite,
      onClick: isGuest
        ? () => promptSignup(<>Save your <span style={{ color: theme.primary }}>favorites</span> as you browse — create a free account now</>)
        : () => {
          if (queue.currentTrack.track.favorite) {
            const favTrack = fav.favorites.filter(
              (favorite) =>
                favorite.track.id ===
                queue.currentTrack.track.id
            );
            fav.removeFromFavorites(
              queue.currentTrack.track.id
            );
            fav.removeTrackFromList(
              queue.currentTrack.track,
              favTrack.id
            );
            queue.currentTrack.track.favorite = false;
          } else {
            fav.addToFavorites(queue.currentTrack.track.id);
            fav.addTrackToFavoritelist(queue.currentTrack.track);
            queue.currentTrack.track.favorite = true;
          }
        },
    },
    {
      type: "open_editor",
      render: ({ size, style }) => (
        <EditorPopoverButton
          track={currentTrack}
          size={size}
          buttonStyle={style}
        />
      ),
    },
  ], [
    currentTrack,
    queue.currentTrack,
    downloadModal,
    fav,
    isGuest,
    playlistVisible,
    promptSignup,
    queue.queueTracker,
    queuePulseActive,
    search
  ]);
  const playerMenuActions = useMemo(() => [
    ...(!auth.isUserPrivate()
      ? [{
        type: "cart",

        label: isFoundInCart(queue.currentTrack.track) ? "Remove from Cart" : "Add to Cart",
        icon: "cart",
        selected: !!isFoundInCart(queue.currentTrack.track),
        onClick: () => {
          const item = isFoundInCart(queue.currentTrack.track);
          if (item) { removeCartItem(item); return; }
          addCartItem(queue.currentTrack.track, "Track");
        },
      }]
      : []),
    { type: "add_queue", label: "Add to Queue", icon: "add_queue", onClick: () => queue.addToQueue(queue.currentTrack.track) },
    {
      type: "collection",
      label: "Add to Playlist",
      icon: "collection",
      onClick: isGuest
        ? () => promptSignup(<>Create quick <span style={{ color: theme.primary }}>collections</span> to organize your tracks. Create your <span style={{ color: theme.primary }}>free account</span></>)
        : addToPlaylistModal.open,
    },
    { type: "similar_tracks", label: "Similar Tracks", icon: "similar_tracks", disabled: true },
    ...(auth.user.adminUser
      ? [{ type: "editor_admin", label: "Editor Admin", icon: "editor_admin", onClick: () => editor.requestTrackOpen(queue.currentTrack.track, false) }]
      : []),
  ], [
    addCartItem,
    addToPlaylistModal.open,
    auth,
    editor,
    isFoundInCart,
    isGuest,
    promptSignup,
    queue,
    removeCartItem
  ]);

  return (
    <>
      {isDesktopOrLaptop ? (
        <Container
          playerActionsPosition={playerActionsPosition}
          queuePanePosition={queueToastPosition}
          showTrackDetails={props.showTrackDetails}
        >
          {queue.addToQueueMessage && (
            <div className="toast-container">
              <div className="toast">
                <Toast openQueue={() => setPlaylistVisible(true)} />
              </div>
            </div>
          )}
          <div className="player-container">
            {/* Thumbnail + Track title + Album Name */}

            {props.showTrackDetails && (
              <div className="details">
                <img
                  className="thumbnail"
                  src={
                    queue.currentTrack &&
                    queue.currentTrack.track &&
                    queue.currentTrack.track.thumbnail
                  }
                  alt="Album cover"
                />
                <div
                  className="title"
                  onClick={() => {
                    props.history.push(
                      `/browse?trackId=${queue.currentTrack &&
                      queue.currentTrack.track &&
                      queue.currentTrack.track.id}`
                    );
                  }}
                >
                  {queue.currentTrack &&
                    queue.currentTrack.track &&
                    queue.currentTrack.track.title}
                </div>
                <div
                  className="album"
                  onClick={() =>
                    props.history.push(
                      `/browse?albumId=${queue.currentTrack &&
                      queue.currentTrack.track &&
                      (queue.currentTrack.track.album.id ||
                        queue.currentTrack.track.album)}`
                    )
                  }
                >
                  {queue.currentTrack.track ? (
                    <div>
                      {queue.currentTrack &&
                        queue.currentTrack.track &&
                        queue.currentTrack.track.variation ? (
                        <div className="album-name">
                          |{" "}
                          {queue.currentTrack &&
                            queue.currentTrack.track &&
                            queue.currentTrack.track.variationTitle}{" "}
                        </div>
                      ) : (
                        <div className="album-name">
                          {" "}
                          {queue.currentTrack &&
                            queue.currentTrack.track &&
                            (queue.currentTrack.track.albumName ||
                              queue.currentTrack.track.album.name)}
                        </div>
                      )}{" "}
                    </div>
                  ) : (
                    lastPlayedTrack &&
                    lastPlayedTrack.track &&
                    lastPlayedTrack.track.variationTitle
                  )}
                </div>
              </div>
            )}

            {/* Previous, Play and Next buttons */}
            <PlaybackControlsContainer
              className="playback-controls"
              showTrackDetails={props.showTrackDetails}
            >
              {/* audit-icons:ignore — transport-control glyph, not a UI icon */}
              <img
                src={playPrevious}
                onClick={
                  queue.playlist[queue.currentTracker - 1] && queue.playPrevious
                }
                alt=""
                className={`change-track ${queue.playlist[queue.currentTracker - 1]
                  ? "active-control"
                  : ""
                  }`}
              />
              {queue.playing ? (
                // audit-icons:ignore — transport-control glyph, not a UI icon
                <img
                  src={pause}
                  alt="pause"
                  onClick={togglePlay}
                  className="toggle-playback"
                />
              ) : (
                // audit-icons:ignore — transport-control glyph, not a UI icon
                <img
                  src={play}
                  alt="play"
                  onClick={togglePlay}
                  className="toggle-playback"
                />
              )}
              {/* audit-icons:ignore — transport-control glyph, not a UI icon */}
              <img
                src={playNext}
                alt=""
                onClick={
                  queue.playlist[queue.currentTracker + 1] && queue.playNext
                }
                className={`change-track ${queue.playlist[queue.currentTracker + 1]
                  ? "active-control"
                  : ""
                  }`}
              />
            </PlaybackControlsContainer>
            {/* !The Waveform component's Grid Placing CSS is in the Component itself */}
            <Waveform isMainPlayer="main-player" />

            {/* Current time and duration of track */}
            <div className="duration">
              {`${currentTime} / ${duration}`}
            </div>

            {/* Volume, queue, editor + overflow menu — standardized strip */}
            <div style={{ marginTop: 12 }}>
              <ActionIconStrip
                wrapperRef={playerActionsRef}
                size="md"
                menuPlacement="top"
                menuMinWidth="200px"
                transparent
                actions={desktopActions}
                menuActions={playerMenuActions}
              />
            </div>

            {/* THE QUEUE PANE */}
            {playlistVisible && (
              <QueuePane
                playlistEl={playlistEl}
                setTracksToSave={setTracksToSave}
                setPlaylistVisible={setPlaylistVisible}
                openModal={createEditPlaylistModal.open}
                queuePanePosition={queueToastPosition}
              />
            )}
          </div>

          {/* Create or Edit playlist modal  */}
          {createEditPlaylistModal.visible && (
            <CreateEditPlaylistModal
              modalState={createEditPlaylistModal}
              type="create"
              tracks={tracksToSave}
            />
          )}

        </Container>
      ) : (
        <MobileContainer
          hasAutoCompleteResults={search.hasAutoCompleteResults()}
        >
          {/* 1st ROW */}
          {queue.addToQueueMessage && (
            <div className="toast-container">
              <div className="toast" style={{ position: "relative" }}>
                <Toast openQueue={() => setMobilePlaylistVisible(true)} />
              </div>
            </div>
          )}
          <div className="mobile-visualization">
            <div className="mobile-waveform">
              <Waveform isMainPlayer="main-player" />
            </div>
            <div className="playback-control">
              {queue.playing ? (
                // audit-icons:ignore — transport-control glyph, not a UI icon
                <img
                  src={pause}
                  alt="pause"
                  onClick={togglePlay}
                  className="toggle-playback"
                />
              ) : (
                // audit-icons:ignore — transport-control glyph, not a UI icon
                <img
                  src={play}
                  alt="play"
                  onClick={togglePlay}
                  className="toggle-playback"
                />
              )}
            </div>
          </div>
          <div className="details-and-queue">
            {/* 2nd ROW */}
            <div className="mobile-details-container">
              <div className="mobile-details">
                <div
                  className="mobile-title"
                  onClick={() => {
                    props.history.push(
                      `/browse?trackId=${queue.currentTrack &&
                      queue.currentTrack.track &&
                      queue.currentTrack.track.id}`
                    );
                  }}
                >
                  {queue.currentTrack.track
                    ? queue.currentTrack.track.title
                    : lastPlayedTrack.track.title}
                </div>
                <div
                  className="mobile-album"
                  onClick={() =>
                    props.history.push(
                      `/browse?albumId=${queue.currentTrack.track.album.id ||
                      queue.currentTrack.track.album}`
                    )
                  }
                >
                  {queue.currentTrack.track ? (
                    <div>
                      {queue.currentTrack.track.variation ? (
                        <div>| {queue.currentTrack.track.variationTitle} </div>
                      ) : (
                        <div>
                          {queue.currentTrack.track.album.name ||
                            queue.currentTrack.track.albumName}
                        </div>
                      )}{" "}
                    </div>
                  ) : (
                    lastPlayedTrack.track.variationTitle
                  )}
                </div>
              </div>
              <div style={{ gridColumn: "5/6", justifySelf: "right", paddingRight: "24px", alignSelf: "center" }}>
                <ActionIconStrip
                  size="sm"
                  menuPlacement="top"
                  menuMinWidth="200px"
                  transparent
                  actions={mobileActions}
                  menuActions={playerMenuActions}
                />
              </div>
            </div>
            {/* Separator */}
            <div className="separator"></div>
            {/* 3rd ROW - THE PLAYLIST_QUEUE */}

            {/* MOBILE QUEUE PANE */}
            {mobilePlaylistVisible && <QueuePane setPlaylistVisible={setMobilePlaylistVisible} />}
          </div>
        </MobileContainer>
      )}
      {queue.playing ? (
        <audio
          ref={audioPlayerEl}
          crossOrigin="anonymous"
          src={
            queue.currentTrack.track
              ? `${process.env.REACT_APP_FILES_URL}/${queue.currentTrack &&
              queue.currentTrack.track &&
              encodeURIComponent(queue.currentTrack.track.mp3File)}`
              : `${process.env.REACT_APP_FILES_URL}/${lastPlayedTrack &&
              lastPlayedTrack.track &&
              encodeURIComponent(lastPlayedTrack.track.mp3File)}`
          }
          controls
          onTimeUpdate={showTime}
          onLoadedData={() => {
            connectAnalyser(audioPlayerEl.current);
            const currentAudioPlayer = audioPlayerEl.current;

            currentAudioPlayer.currentTime =
              currentAudioPlayer.duration * (queue.percentagePlayedRef.current / 100);
            setCurrentTimeAndDuration();

            if (queue.playing) {
              currentAudioPlayer.play().catch(() => queue.setPlaying(false));
            }
          }}
          onEnded={playNextAndResetProgress}
          style={{ display: "none" }}
        ></audio>
      ) : (
        <audio ref={audioPlayerEl} crossOrigin="anonymous" src=""></audio>
      )}
      <DownloadModal modalState={downloadModal} />
      {addToPlaylistModal.visible && (
        <AddToPlaylistModal
          modalState={addToPlaylistModal}
          track={queue.currentTrack.track}
        />
      )}
    </>
  );
};

export default withRouter(Player);
